import { authAdapter } from '@/app/_auth/auth'
import { useAuth } from '@/app/_auth/auth-hooks'
import { TabsPanelId, useSplitView } from '@/app/_common/split-view/split-view-context'
import { CraftEmergenceIcon } from '@/components/icons/craft-emergence-icon'
import { LogoutRightIcon } from '@/components/icons/logout-right-icon'
import { SettingsIcon } from '@/components/icons/settings-icon'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn, GA_EVENTS } from '@/lib/utils'
// import { TooltipContentProps } from '@radix-ui/react-tooltip'
import { useTranslate } from '@tolgee/react'
import { cva } from 'class-variance-authority'
import { BookOpenIcon } from 'lucide-react'
import ReactGA from 'react-ga4'
import { createTab } from './split-view/tabs/factories'
import { TabTypeStatic } from './split-view/tabs/types'

export function Header({ className }: Readonly<{ className?: string }>) {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { addTab } = useSplitView()
  return (
    <header
      className={cn(
        'sticky top-0 z-10 flex h-[56px] w-full shrink-0 items-center justify-between bg-neutral-600 pl-5 pr-3',
        className,
      )}
    >
      <div className='flex items-center gap-4' data-gtmid='navbar-logo-link'>
        <div className='pt-1'>
          <CraftEmergenceIcon />
        </div>
      </div>
      {user && (
        <div className='flex items-center gap-2'>
          <div className='flex items-center gap-1'>
            <div className='m-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-white'>
              <span className='text-xs font-normal text-neutral-600'>{user?.firstName?.[0]}</span>
            </div>
            <p className='text-nowrap text-xs font-light text-neutral-white'>{user?.fullName}</p>
          </div>
          <NavLinkWithAnalytics
            tooltip={t('navigation.settings.title')}
            analyticsLabel='navigate_settings'
            className='px-2'
            onClick={() => {
              addTab({
                tab: createTab({ type: TabTypeStatic.Settings }),
                panelId: TabsPanelId.Right,
              })
            }}
          >
            <SettingsIcon className='size-6 shrink-0 text-neutral-white' />
          </NavLinkWithAnalytics>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <a
                href='https://docs.emergence.ai/craft'
                target='_blank'
                aria-label='Craft Documentation'
                rel='noreferrer'
                className={navItemClasses()}
              >
                <BookOpenIcon strokeWidth={1} className='size-5 shrink-0 text-neutral-white' />
              </a>
            </TooltipTrigger>
            <TooltipContent
              size='sm'
              side='bottom'
              align='end'
              sideOffset={12}
              className='h-fit max-w-80 whitespace-pre-line'
            >
              Craft Documentation
            </TooltipContent>
          </Tooltip>
          <NavLinkWithAnalytics
            tooltip={t('navigation.logout.title')}
            analyticsLabel='navigate_logout'
            onClick={() => {
              if (authAdapter) {
                authAdapter.signOut().catch((err) => {
                  console.error('Failed to sign out:', err)
                })
              }
            }}
          >
            <LogoutRightIcon className='w-8 shrink-0' />
          </NavLinkWithAnalytics>
        </div>
      )}
    </header>
  )
}

// Component Types
interface NavLinkProps {
  tooltip?: string | null
  className?: string
  children: React.ReactNode
  onClick: (e: React.MouseEvent) => void
}

interface NavLinkWithAnalyticsProps extends NavLinkProps {
  analyticsLabel: string
}

// 👇 nav components

const navItemClasses = cva(
  'p-1 flex items-center whitespace-nowrap gap-2 size-10 w-full cursor-pointer select-none rounded text-sm font-normal text-neutral-500 outline-none hover:bg-neutral-25 hover:bg-opacity-[0.48] focus:bg-opacity-[0.32]',
)

// Base NavLink component
const NavLink = ({ tooltip, className, children, ...props }: NavLinkProps) => (
  <Tooltip delayDuration={0}>
    <TooltipTrigger asChild>
      <Button
        unstyled
        className={cn(navItemClasses(), className)}
        {...(tooltip && { 'aria-label': tooltip })}
        {...props}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent
      size='sm'
      side='bottom'
      align='end'
      sideOffset={12}
      className={cn('h-fit max-w-80 whitespace-pre-line', !tooltip && 'hidden')}
    >
      {tooltip}
    </TooltipContent>
  </Tooltip>
)

// NavLink with analytics tracking
const NavLinkWithAnalytics = ({
  analyticsLabel,
  onClick,
  children,
  ...props
}: NavLinkWithAnalyticsProps) => {
  const handleClick = (e: React.MouseEvent) => {
    ReactGA.event({
      action: GA_EVENTS.ACTION.BTN,
      category: GA_EVENTS.CATEGORY.NAV,
      label: analyticsLabel,
    })

    if (onClick) {
      onClick(e)
    }
  }

  return (
    <NavLink {...props} onClick={handleClick}>
      {children}
    </NavLink>
  )
}
